/**
 * Onboarding redirect resolution utilities
 *
 * This module provides pure functions to determine redirect targets based on
 * onboarding state. These functions are used by route loaders to implement
 * automatic navigation during the onboarding flow.
 *
 * @see apps/web/src/routes/onboarding/route.tsx - Main onboarding layout route
 * @see docs/frontend/routing-and-navigation.md - Onboarding state machine documentation
 */

import type { OnboardingStatusResponse } from "@/lib/api/generated";
import { redirect } from "@tanstack/react-router";
import { logInfo } from "@/lib/structured-log";
import { normalizePath } from "@/lib/utils/path";

/**
 * Possible redirect targets during onboarding flow
 *
 * Backend: Onboarding states defined in apps/api/domain/onboarding/state.py
 *
 * State transitions:
 * - registered → treated as activated for legacy users after open signup
 * - waitlist_submitted → treated as activated for legacy users after open signup
 * - activated + profile incomplete → /onboarding/profile
 * - activated + profile complete → /onboarding/profile (profile resubmission repairs legacy data)
 * - workspace_created + profile incomplete → /onboarding/profile
 * - workspace_created + profile complete → /workspace/{slug}/new-task
 */
export type RedirectTarget =
  | {
      to:
        | "/onboarding/activation"
        | "/onboarding/waitlist"
        | "/onboarding/waitlist/pending"
        | "/onboarding/profile"
        | "/workspace/new-task";
    }
  | { to: "/workspace/$workspaceSlug/new-task"; params: { workspaceSlug: string } };

interface RedirectSearchParams {
  return_to?: string;
}

/**
 * Resolve redirect target based on onboarding status and current pathname
 *
 * @deprecated Use `resolveRedirectTargetEnhanced` instead, which handles all edge cases
 * including workspace access blocking and data inconsistency scenarios.
 *
 * This is the basic version that only handles /onboarding path redirects.
 * It does NOT block unauthorized workspace access or handle data corruption cases.
 *
 * **Important**: The pathname parameter must be normalized (no trailing slash)
 * using the `normalizePath` function before calling this function.
 *
 * @param status - Current user's onboarding status from backend
 * @param pathname - Normalized current pathname (no trailing slash)
 * @returns Redirect target or null if user should stay on current page
 *
 * @see resolveRedirectTargetEnhanced - Use this instead for route guards
 */
export function resolveRedirectTarget(
  status: OnboardingStatusResponse,
  pathname: string,
): RedirectTarget | null {
  const workspaceSlug = status.workspace_slug ?? null;

  // workspace_created: User has completed onboarding, redirect to workspace
  if (status.onboarding_state === "workspace_created" && workspaceSlug) {
    return {
      to: "/workspace/$workspaceSlug/new-task",
      params: { workspaceSlug },
    };
  }

  // Open signup: legacy registered/waitlist users now follow the same flow as activated.
  if (
    status.onboarding_state === "activated" ||
    status.onboarding_state === "registered" ||
    status.onboarding_state === "waitlist_submitted"
  ) {
    if (pathname === "/onboarding") {
      return { to: "/onboarding/profile" };
    }
    return null;
  }

  return null;
}

/**
 * Convert redirect target to actual pathname string
 *
 * This helper function resolves the TanStack Router parameterized path
 * to an actual pathname that can be used for comparison.
 *
 * @param target - Redirect target object
 * @returns Resolved pathname string
 *
 * @example
 * ```ts
 * const target = { to: "/workspace/$workspaceSlug/new-task", params: { workspaceSlug: "acme" } };
 * const path = resolveTargetPath(target); // "/workspace/acme/new-task"
 * ```
 */
export function resolveTargetPath(target: RedirectTarget): string {
  if (target.to === "/workspace/$workspaceSlug/new-task") {
    return `/workspace/${target.params.workspaceSlug}/new-task`;
  }

  return target.to;
}

/**
 * Enhanced state machine for route guard integration
 *
 * This is the enhanced version of `resolveRedirectTarget` that handles all edge cases
 * and is designed to be called from `beforeLoad` route guards. It prevents unauthorized
 * access to workspace routes and handles data inconsistency scenarios.
 *
 * Key improvements over the basic version:
 * - Blocks workspace access for non-workspace_created states
 * - Handles workspace_id/workspace_slug inconsistencies
 * - Provides comprehensive logging for debugging
 * - Works with all routes, not just /onboarding paths
 *
 * @param status - Current user's onboarding status from backend
 * @param pathname - Normalized current pathname (no trailing slash)
 * @returns Redirect target or null if user should stay on current page
 *
 * @example
 * ```ts
 * // In beforeLoad route guard
 * const status = await loadOnboardingStatus();
 * const target = resolveRedirectTargetEnhanced(status, pathname);
 * if (target) {
 *   const desiredPath = resolveTargetPath(target);
 *   if (desiredPath !== pathname) {
 *     throw redirect(target);
 *   }
 * }
 * ```
 */
export function resolveRedirectTargetEnhanced(
  status: OnboardingStatusResponse,
  pathname: string,
): RedirectTarget | null {
  const workspaceSlug = status.workspace_slug ?? null;
  const workspaceId = status.workspace_id ?? null;
  const usesWorkspaceWizard =
    (status.onboarding_flow_version ?? 1) >= 2 && !status.onboarding_completed;

  if (usesWorkspaceWizard) {
    if (workspaceSlug && workspaceId) {
      const wizardPath = `/workspace/${workspaceSlug}/new-task`;
      if (pathname === wizardPath) {
        return null;
      }
      return {
        to: "/workspace/$workspaceSlug/new-task",
        params: { workspaceSlug },
      };
    }

    if (pathname === "/workspace/new-task") {
      return null;
    }
    return { to: "/workspace/new-task" };
  }

  // ===== 1. workspace_created: User has completed onboarding =====
  if (status.onboarding_state === "workspace_created") {
    const hasCompleteWorkspaceData = workspaceSlug && workspaceId;

    // Profile not completed — redirect to profile only during onboarding flow.
    // Workspace access is NOT blocked: existing users who had workspace_created
    // before profile fields were introduced should still reach their workspace.
    if (!status.profile_completed) {
      if (pathname === "/onboarding/profile") {
        return null;
      }
      if (pathname.startsWith("/onboarding")) {
        return { to: "/onboarding/profile" };
      }
      // For workspace / signin / signup / root: send to workspace if data exists,
      // otherwise fall through to workspace-data-missing handling below.
      if (hasCompleteWorkspaceData) {
        if (pathname === "/" || pathname.startsWith("/signin") || pathname.startsWith("/signup")) {
          return {
            to: "/workspace/$workspaceSlug/new-task",
            params: { workspaceSlug },
          };
        }
        // Already on a workspace route — allow access
        return null;
      }
    }

    // Data inconsistency: workspace_created but missing workspace data. Sending the
    // user back through profile submission lets the backend repair it automatically.
    if (!hasCompleteWorkspaceData) {
      if (pathname === "/onboarding/profile") {
        return null;
      }
      if (pathname.startsWith("/onboarding") || pathname.startsWith("/workspace")) {
        return { to: "/onboarding/profile" };
      }
      return null;
    }

    // Normal case: workspace data is complete, profile is complete
    if (
      pathname === "/" ||
      pathname.startsWith("/onboarding") ||
      pathname.startsWith("/signin") ||
      pathname.startsWith("/signup")
    ) {
      return {
        to: "/workspace/$workspaceSlug/new-task",
        params: { workspaceSlug },
      };
    }

    // User is in correct state, allow access to workspace
    return null;
  }

  // ===== 2. Pre-workspace states: profile submission creates workspace =====
  if (
    status.onboarding_state === "activated" ||
    status.onboarding_state === "registered" ||
    status.onboarding_state === "waitlist_submitted"
  ) {
    // Block workspace access until profile submission creates the workspace.
    if (pathname.startsWith("/workspace")) {
      return { to: "/onboarding/profile" };
    }

    // Profile not yet completed
    if (!status.profile_completed) {
      if (pathname === "/onboarding/profile") {
        return null;
      }
      if (pathname === "/") {
        return { to: "/onboarding/profile" };
      }
      if (pathname.startsWith("/onboarding")) {
        return { to: "/onboarding/profile" };
      }
      return null;
    }

    // A legacy user can have complete profile fields without a workspace. Keep them
    // on the profile page so resubmission invokes backend auto-creation.
    if (pathname === "/onboarding/profile") {
      return null;
    }
    if (pathname === "/") {
      return { to: "/onboarding/profile" };
    }
    if (pathname.startsWith("/onboarding")) {
      return { to: "/onboarding/profile" };
    }

    return null;
  }

  // Unknown state - allow access (defensive)
  return null;
}

/**
 * Execute state machine redirects based on onboarding status
 *
 * This helper function encapsulates the common logic for executing state machine
 * redirects. It checks if the user should be redirected to a different route based
 * on their onboarding state and current location.
 *
 * @param status - The user's onboarding status
 * @param pathname - The current pathname
 * @param logSource - The log source identifier for tracking (e.g., "route.loader")
 * @throws {redirect} - If a redirect is needed
 */
export function executeStateMachineRedirect(
  status: OnboardingStatusResponse,
  pathname: string,
  logSource: string,
  search?: RedirectSearchParams,
): void {
  const normalizedPath = normalizePath(pathname);
  const target = resolveRedirectTargetEnhanced(status, normalizedPath);
  if (target) {
    const desiredPath = resolveTargetPath(target);

    // Only redirect if the desired path is different from current path
    // This prevents redirect loops when user is already on the target page
    if (desiredPath !== normalizedPath) {
      logInfo(`${logSource}.redirect`, {
        from: normalizedPath,
        to: desiredPath,
        state: status.onboarding_state,
      });
      throw redirect({
        ...target,
        search,
      });
    }
  }
}
